import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Lock, Mail, ArrowRight } from "lucide-react";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      if (forgotMode) {
        await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }), credentials: "include",
        });
        setResetSent(true);
      } else {
        await login(email, password);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-black flex" data-testid="login-page">
      {/* Left panel - big brand */}
      <div className="hidden lg:flex lg:w-[55%] flex-col justify-center px-20 relative overflow-hidden">
        <div className="relative z-10">
          <h1 className="font-heading text-[7rem] xl:text-[9rem] font-bold tracking-tighter text-white leading-[0.85] uppercase">
            The<br />Vault
          </h1>
        </div>
        {/* Background accent */}
        <div className="absolute -bottom-20 -right-20 w-96 h-96 border border-white/5 rounded-full"></div>
        <div className="absolute -top-10 -left-10 w-64 h-64 border border-white/5 rounded-full"></div>
      </div>

      {/* Right panel - form */}
      <div className="flex-1 flex items-center justify-center px-6 bg-white rounded-l-3xl lg:rounded-l-[2rem]">
        <div className="w-full max-w-sm">
          {/* Mobile brand */}
          <div className="lg:hidden mb-10">
            <h1 className="font-heading text-5xl font-bold tracking-tighter text-black uppercase">The Vault</h1>
            <div className="mt-2 h-px w-12 bg-black/20"></div>
          </div>

          <div className="mb-8">
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-gray-900">
              {forgotMode ? "Reset password" : "Sign in"}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {forgotMode ? "Enter your email to receive a reset link" : "Enter your credentials to continue"}
            </p>
          </div>

          {error && (
            <div data-testid="auth-error" className="mb-4 p-3 border border-red-200 bg-red-50 rounded-sm text-sm text-red-700">
              {error}
            </div>
          )}

          {resetSent ? (
            <div className="p-4 border border-green-200 bg-green-50 rounded-sm text-sm text-green-700">
              <p className="font-medium">Reset link sent</p>
              <p className="text-xs mt-1 text-green-600">Check your email (or server console logs) for the password reset link.</p>
              <button onClick={() => { setForgotMode(false); setResetSent(false); }} className="mt-3 text-xs text-green-800 underline">Back to sign in</button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" strokeWidth={1.5} />
                  <Input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-10 rounded-sm focus:ring-1 focus:ring-black focus:border-black" placeholder="you@example.com" required />
                </div>
              </div>
              {!forgotMode && (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" strokeWidth={1.5} />
                    <Input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="pl-10 rounded-sm focus:ring-1 focus:ring-black focus:border-black" placeholder="Enter password" required />
                  </div>
                </div>
              )}
              <Button data-testid="login-form-submit-button" type="submit" disabled={submitting} className="w-full rounded-sm bg-black text-white hover:bg-gray-800 gap-2">
                {submitting ? "Please wait..." : (forgotMode ? "Send Reset Link" : "Sign In")}
                <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Button>
              <div className="text-center">
                <button data-testid="forgot-password-btn" type="button" onClick={() => { setForgotMode(!forgotMode); setError(""); }} className="text-xs text-gray-500 hover:text-gray-900 transition-colors">
                  {forgotMode ? "Back to sign in" : "Forgot password?"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
